import React, { useState, useEffect } from 'react';
import { Card, Progress, Button, Form, InputNumber, DatePicker, Select, Input, message, Space, Typography, Alert, Divider, Tag, Spin, Statistic, Modal, Radio } from 'antd';
import { DollarOutlined, BankOutlined, CheckCircleOutlined, ClockCircleOutlined, WarningOutlined, InfoCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useFeature } from '../contexts/FeatureFlagsContext';
import api, { ContributionStatus } from '../api';
import { formatCurrency } from '../utils/formatters';
import { paymentsEndpoints, type BankAccount } from '../api/paymentsEndpoints';
import dayjs from 'dayjs';

const { Text, Title } = Typography;
const { TextArea } = Input;

interface FundingStatusProps {
  onStatusChange?: () => void;
}

export const FundingStatus: React.FC<FundingStatusProps> = ({ onStatusChange }) => {
  const { t } = useTranslation('funding');
  const isPlaidEnabled = useFeature('plaid');
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<ContributionStatus | null>(null);
  const [showForm, setShowForm] = useState(false);
  
  // S-C1: Bank deposit state
  const [fundingMethod, setFundingMethod] = useState<'bank' | 'manual' | null>(null);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [loadingBankAccounts, setLoadingBankAccounts] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [creatingDeposit, setCreatingDeposit] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const response = await api.contributions.getStatus();
      setStatus(response);
    } catch (error: any) {
      console.error('Failed to fetch contribution status:', error);
      message.error(error.message || t('errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // S-C1: Fetch bank accounts
  const fetchBankAccounts = async () => {
    try {
      setLoadingBankAccounts(true);
      const response = await paymentsEndpoints.listBankAccounts();
      setBankAccounts(response.accounts);
    } catch (error: any) {
      console.error('Failed to fetch bank accounts:', error);
    } finally {
      setLoadingBankAccounts(false);
    }
  };

  // S-C1: Handle bank deposit creation
  const handleCreateDeposit = async () => {
    try {
      setCreatingDeposit(true);
      await api.contributions.createInitialDeposit();
      message.success(t('bankDeposit.success.created'));
      setShowDepositModal(false);
      await fetchStatus();
      onStatusChange?.();
    } catch (error: any) {
      console.error('Failed to create deposit:', error);
      const errorKey = error.response?.data?.error?.includes('bank account first')
        ? 'noBankAccount'
        : error.response?.data?.error?.includes('default bank account')
        ? 'noDefaultAccount'
        : error.response?.data?.error?.includes('already eligible')
        ? 'alreadyEligible'
        : error.response?.data?.error?.includes('pending')
        ? 'pendingDeposit'
        : 'createFailed';
      message.error(t(`bankDeposit.errors.${errorKey}`));
    } finally {
      setCreatingDeposit(false);
    }
  };

  // S-C2: Handle transfer status refresh
  const handleRefreshStatus = async () => {
    if (!status?.pending_deposit?.id) return;

    try {
      setLoading(true);
      await api.contributions.refreshTransferStatus(status.pending_deposit.id);
      message.success(t('bankDeposit.refresh.success'));
      await fetchStatus();
      onStatusChange?.();
    } catch (error: any) {
      console.error('Failed to refresh status:', error);
      message.error(t('bankDeposit.refresh.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    if (!status) return;

    try {
      setSubmitting(true);
      
      const submitData = {
        amount_cents: Math.round(values.amount * 100),
        sent_at: values.sent_at.format('YYYY-MM-DD'),
        payment_method: values.payment_method,
        reference: values.reference,
        note: values.note,
      };

      await api.contributions.submit(submitData);
      
      message.success(t('form.submitSuccess'));
      form.resetFields();
      setShowForm(false);
      await fetchStatus();
      onStatusChange?.();
    } catch (error: any) {
      console.error('Failed to submit contribution:', error);
      message.error(error.message || t('form.submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (!status) {
    return null;
  }

  // If user is eligible, show compact summary
  if (status.eligible) {
    return (
      <Card>
        <Space orientation="vertical" size="small" style={{ width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Space>
              <CheckCircleOutlined style={{ fontSize: 24, color: '#52c41a' }} />
              <div>
                <Title level={4} style={{ margin: 0 }}>{t('eligible.title')}</Title>
                <Text type="secondary">{t('eligible.subtitle')}</Text>
              </div>
            </Space>
            <Statistic
              title={t('eligible.basePrincipal')}
              value={status.base_principal_cents / 100}
              prefix="$"
              precision={2}
            />
          </div>
        </Space>
      </Card>
    );
  }

  const percentFunded = status.required_cents > 0 
    ? (status.funded_cents / status.required_cents) * 100 
    : 0;

  const getStatusColor = () => {
    switch (status.status) {
      case 'eligible': return 'success';
      case 'pending_review': return 'processing';
      case 'partially_funded': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = () => {
    switch (status.status) {
      case 'eligible': return <CheckCircleOutlined />;
      case 'pending_review': return <ClockCircleOutlined />;
      case 'partially_funded': return <WarningOutlined />;
      default: return <InfoCircleOutlined />;
    }
  };

  return (
    <Card
      title={
        <Space>
          <BankOutlined />
          <span>{t('card.title')}</span>
          <Tag color={getStatusColor()} icon={getStatusIcon()}>
            {t(`status.${status.status}`)}
          </Tag>
        </Space>
      }
    >
      <Space orientation="vertical" size="large" style={{ width: '100%' }}>
        {/* Progress Overview */}
        <div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <Text strong>{t('progress.required')}</Text>
              <Text strong>{formatCurrency(status.required_cents)}</Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <Text type="success">{t('progress.funded')}</Text>
              <Text type="success">{formatCurrency(status.funded_cents)}</Text>
            </div>
            {status.pending_cents > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <Text type="warning">{t('progress.pending')}</Text>
                <Text type="warning">{formatCurrency(status.pending_cents)}</Text>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <Text type={status.remaining_cents > 0 ? 'danger' : 'secondary'}>
                {t('progress.remaining')}
              </Text>
              <Text type={status.remaining_cents > 0 ? 'danger' : 'secondary'}>
                {formatCurrency(status.remaining_cents)}
              </Text>
            </div>
          </div>
          
          <Progress 
            percent={Math.min(percentFunded, 100)} 
            status={status.status === 'eligible' ? 'success' : 'active'}
            strokeColor={{
              '0%': '#108ee9',
              '100%': '#87d068',
            }}
          />
        </div>

        {/* Status-specific alerts */}
        {status.status === 'pending_review' && (
          <Alert
            title={t('alerts.pendingReview.title')}
            description={t('alerts.pendingReview.description')}
            type="info"
            showIcon
            icon={<ClockCircleOutlined />}
          />
        )}

        {status.status === 'not_funded' && (
          <Alert
            title={t('alerts.notFunded.title')}
            description={t('alerts.notFunded.description')}
            type="warning"
            showIcon
          />
        )}

        {/* S-C1/C2: Pending Deposit Status */}
        {status.pending_deposit && (
          <Card>
            <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
              {/* S-C3: Show success when deposit completes */}
              {status.pending_deposit.status === 'succeeded' && (
                <Alert
                  title={t('bankDeposit.status.completedTitle')}
                  description={t('bankDeposit.status.completedDescription')}
                  type="success"
                  showIcon
                  icon={<CheckCircleOutlined />}
                />
              )}
              
              {status.pending_deposit.status !== 'succeeded' && (
                <Alert
                  title={t(`bankDeposit.status.${status.pending_deposit.status}`)}
                  description={t(`bankDeposit.status.${status.pending_deposit.status}Description`)}
                  type={status.pending_deposit.status === 'failed' ? 'error' : 'info'}
                  showIcon
                  icon={status.pending_deposit.status === 'processing' ? <ClockCircleOutlined /> : undefined}
                />
              )}
              
              <div>
                <Space orientation="vertical" size="small" style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text type="secondary">{t('bankDeposit.status.amount')}:</Text>
                    <Text strong>{formatCurrency(status.pending_deposit.amount_cents)}</Text>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text type="secondary">{t('bankDeposit.status.created')}:</Text>
                    <Text>{dayjs(status.pending_deposit.created_at).format('MMM D, YYYY h:mm A')}</Text>
                  </div>
                  {status.pending_deposit.provider_transfer_id && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text type="secondary">{t('bankDeposit.status.transferId')}:</Text>
                      <Text code style={{ fontSize: '0.85em' }}>{status.pending_deposit.provider_transfer_id}</Text>
                    </div>
                  )}
                  {status.pending_deposit.failure_reason && (
                    <div>
                      <Text type="danger">{t('bankDeposit.status.failureReason')}:</Text>
                      <br />
                      <Text type="danger">{status.pending_deposit.failure_reason}</Text>
                    </div>
                  )}
                </Space>
              </div>

              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                {(status.pending_deposit.status === 'pending' || status.pending_deposit.status === 'processing') && (
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={handleRefreshStatus}
                    loading={loading}
                  >
                    {t('bankDeposit.refresh.button')}
                  </Button>
                )}
                {status.pending_deposit.status === 'failed' && (
                  <Button type="primary" onClick={() => setShowDepositModal(true)}>
                    {t('bankDeposit.authorize.retry')}
                  </Button>
                )}
              </Space>
            </Space>
          </Card>
        )}

        {/* S-C1: Funding Method Choice (only show if no pending deposit and not funded) */}
        {!status.pending_deposit && status.remaining_cents > 0 && !fundingMethod && (
          <Card>
            <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
              <div>
                <Title level={5}>{t('bankDeposit.methodChoice.title')}</Title>
                <Text type="secondary">{t('bankDeposit.methodChoice.description')}</Text>
              </div>
              <Radio.Group
                style={{ width: '100%' }}
                onChange={(e) => {
                  setFundingMethod(e.target.value);
                  if (e.target.value === 'bank') {
                    fetchBankAccounts();
                  }
                }}
              >
                <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
                  {isPlaidEnabled && (
                    <Card
                      hoverable
                      size="small"
                      onClick={() => {
                        setFundingMethod('bank');
                        fetchBankAccounts();
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <Radio value="bank">
                        <Space orientation="vertical" size={4}>
                          <Text strong>{t('bankDeposit.methodChoice.bankOption')}</Text>
                          <Text type="secondary" style={{ fontSize: '0.9em' }}>
                            {t('bankDeposit.methodChoice.bankDescription')}
                          </Text>
                        </Space>
                      </Radio>
                    </Card>
                  )}
                  <Card
                    hoverable
                    size="small"
                    onClick={() => setFundingMethod('manual')}
                    style={{ cursor: 'pointer' }}
                  >
                    <Radio value="manual">
                      <Space orientation="vertical" size={4}>
                        <Text strong>{t('bankDeposit.methodChoice.manualOption')}</Text>
                        <Text type="secondary" style={{ fontSize: '0.9em' }}>
                          {t('bankDeposit.methodChoice.manualDescription')}
                        </Text>
                      </Space>
                    </Radio>
                  </Card>
                </Space>
              </Radio.Group>
            </Space>
          </Card>
        )}

        {/* S-C1: Bank Deposit Button */}
        {fundingMethod === 'bank' && !status.pending_deposit && status.remaining_cents > 0 && (
          <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
            <Button
              type="primary"
              size="large"
              icon={<BankOutlined />}
              onClick={() => setShowDepositModal(true)}
              loading={loadingBankAccounts}
              block
            >
              {t('bankDeposit.authorize.button')}
            </Button>
            <Button
              type="link"
              size="small"
              onClick={() => setFundingMethod('manual')}
              block
            >
              {t('bankDeposit.backToManual')}
            </Button>
          </Space>
        )}

        {/* Pending Contributions */}
        {status.pending_contributions && status.pending_contributions.length > 0 && (
          <div>
            <Divider styles={{ content: {margin: 0} }}>{t('pending.title')}</Divider>
            <Space orientation="vertical" size="small" style={{ width: '100%' }}>
              {status.pending_contributions.map((contrib) => (
                <Card key={contrib.id} size="small" type="inner">
                  <Space orientation="vertical" size="small" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text strong>{formatCurrency(contrib.amount_cents)}</Text>
                      <Tag color="processing">{t('status.pending')}</Tag>
                    </div>
                    <Text type="secondary">
                      {t('pending.sentOn')}: {dayjs(contrib.sent_at).format('MMM D, YYYY')}
                    </Text>
                    <Text type="secondary">
                      {t('pending.method')}: {t(`paymentMethods.${contrib.payment_method}`)}
                    </Text>
                    {contrib.reference && (
                      <Text type="secondary">
                        {t('pending.reference')}: {contrib.reference}
                      </Text>
                    )}
                  </Space>
                </Card>
              ))}
            </Space>
          </div>
        )}
        
        {/* S-C1: Manual Contribution Form - Only show when manual method selected */}
        {fundingMethod === 'manual' && (
          <>
            {showForm ? (
              <>
                <Divider styles={{ content: {margin: 0} }}>{t('form.title')}</Divider>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              initialValues={{
                sent_at: dayjs(),
              }}
            >
              <Form.Item
                name="amount"
                label={t('form.amount.label')}
                rules={[
                  { required: true, message: t('form.amount.required') },
                  { type: 'number', min: 0.01, message: t('form.amount.min') },
                ]}
                extra={status.remaining_cents > 0 ? t('form.amount.remaining', { amount: formatCurrency(status.remaining_cents) }) : undefined}
              >
                <InputNumber
                  prefix={<DollarOutlined />}
                  style={{ width: '100%' }}
                  placeholder={t('form.amount.placeholder')}
                  precision={2}
                  min={0}
                />
              </Form.Item>

              <Form.Item
                name="sent_at"
                label={t('form.sentAt.label')}
                rules={[{ required: true, message: t('form.sentAt.required') }]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format="YYYY-MM-DD"
                  disabledDate={(current) => current && current > dayjs().endOf('day')}
                />
              </Form.Item>

              <Form.Item
                name="payment_method"
                label={t('form.paymentMethod.label')}
                rules={[{ required: true, message: t('form.paymentMethod.required') }]}
              >
                <Select placeholder={t('form.paymentMethod.placeholder')}>
                  {status.payment_methods.map((method) => (
                    <Select.Option key={method.value} value={method.value}>
                      {method.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="reference"
                label={t('form.reference.label')}
                extra={t('form.reference.help')}
              >
                <Input placeholder={t('form.reference.placeholder')} />
              </Form.Item>

              <Form.Item
                name="note"
                label={t('form.note.label')}
                extra={t('form.note.help')}
              >
                <TextArea 
                  rows={2} 
                  placeholder={t('form.note.placeholder')}
                  maxLength={500}
                />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" loading={submitting}>
                    {t('form.submit')}
                  </Button>
                  <Button onClick={() => setShowForm(false)}>
                    {t('form.cancel')}
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </>
        ) : (
          <Button
            type="primary"
            size="large"
            icon={<DollarOutlined />}
            onClick={() => setShowForm(true)}
            block
          >
            {t('form.logContribution')}
          </Button>
        )}
          </>
        )}
      </Space>

      {/* S-C1: Deposit Authorization Modal */}
      <Modal
        title={t('bankDeposit.authorize.title')}
        open={showDepositModal}
        onOk={handleCreateDeposit}
        onCancel={() => setShowDepositModal(false)}
        okText={t('bankDeposit.authorize.confirm')}
        cancelText={t('bankDeposit.authorize.cancel')}
        confirmLoading={creatingDeposit}
      >
        <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
          <Text>{t('bankDeposit.authorize.description')}</Text>
          
          <div>
            <Text strong>{t('bankDeposit.authorize.amount')}:</Text>
            <div style={{ fontSize: '1.5em', color: '#1677ff', marginTop: 8 }}>
              {formatCurrency(status?.remaining_cents || 0)}
            </div>
          </div>

          {bankAccounts.length > 0 && (
            <div>
              <Text strong>{t('bankDeposit.authorize.bankAccount')}:</Text>
              {bankAccounts.filter(acc => acc.is_default).map(acc => (
                <Card key={acc.id} size="small" style={{ marginTop: 8 }}>
                  <Space>
                    <BankOutlined style={{ fontSize: 20, color: '#1677ff' }} />
                    <div>
                      <Text strong>{acc.institution_name}</Text>
                      <br />
                      <Text type="secondary">
                        {acc.account_name} ••••{acc.mask}
                      </Text>
                    </div>
                  </Space>
                </Card>
              ))}
            </div>
          )}

          <Alert
            type="info"
            title="ACH deposits typically take 1-3 business days to process"
            showIcon
          />
        </Space>
      </Modal>
    </Card>
  );
};
